import { useCallback, useReducer, useEffect } from 'react';

function fireClickEvent(element, eventInit) {
  const event =
    typeof PointerEvent !== 'undefined'
      ? new PointerEvent('click', eventInit)
      : new MouseEvent('click', eventInit);
  return element.dispatchEvent(event);
}

function quantityPickerReducer(state, action) {
  switch (action.type) {
    case 'set': {
      const qty = Math.min(Math.max(action.value, state.min), state.max);
      return { ...state, qty: !!action.callback?.(qty) ? qty : state.qty };
    }
    case 'increment': {
      const qty = Math.min(state.max, state.qty + state.step);
      return { ...state, qty: !!action.callback?.(qty) ? qty : state.qty };
    }
    case 'decrement': {
      const qty = Math.max(state.min, state.qty - state.step);
      return { ...state, qty: !!action.callback?.(qty) ? qty : state.qty };
    }
    case 'sync': {
      return { ...state, qty: action.value };
    }
    default: {
      throw new Error(`Unhandled action type`);
    }
  }
}

export const QuantityPicker = (props) => {
  const [state, dispatch] = useReducer(quantityPickerReducer, {
    qty: props.value ?? props.defaultValue ?? 0,
    min: props.min ?? 0,
    max: props.max ?? Number.MAX_SAFE_INTEGER,
    step: props.step ?? 1,
  });

  // Sync with external value prop
  useEffect(() => {
    if (props.value !== undefined && props.value !== state.qty) {
      dispatch({ type: 'sync', value: props.value });
    }
  }, [props.value]);

  const onInputKeyDown = useCallback(
    (event) => {
      const allowedKeys = [
        'Tab',
        'Enter',
        'Delete',
        'Backspace',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
      ];

      if (!allowedKeys.includes(event.key) && isNaN(Number(event.key))) {
        event.preventDefault();
        return false;
      }
    },
    []
  );

  const onButtonKeyDown = useCallback(
    (event) => {
      const element = event.currentTarget;
      const isEnter = event.keyCode === 13;
      const isSpace = event.keyCode === 32;

      if (isEnter || isSpace) {
        event.preventDefault();
        fireClickEvent(element, event);
      }
    },
    []
  );

  const onButtonClick = (type) => () => {
    dispatch({ type, callback: props.onChange });
  };

  const onChange = useCallback(
    (event) => {
      if (event.defaultPrevented) return;
      if (!event.defaultPrevented && event.target instanceof HTMLInputElement) {
        const value = Number(event.target.value);
        dispatch({ type: 'set', value, callback: props.onChange });
      }
    },
    [props.onChange]
  );

  return (
    <div style={{
      width: '100%',
      background: '#F5F5F5',
      overflow: 'hidden',
      borderRadius: 12,
      justifyContent: 'flex-start',
      alignItems: 'center',
      display: 'flex',
      height: 48
    }}>
      <button
        type="button"
        onClick={onButtonClick('increment')}
        onKeyDown={onButtonKeyDown}
        style={{
          minWidth: 36,
          width: 36,
          height: 48,
          justifyContent: 'center',
          alignItems: 'center',
          gap: 4,
          display: 'flex',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          flexShrink: 0
        }}>
        <div style={{ width: 20, height: 20, position: 'relative' }}>
          <div style={{
            width: 11.67,
            height: 1.67,
            left: 4.17,
            top: 9.17,
            position: 'absolute',
            background: '#0A0D14'
          }} />
          <div style={{
            width: 1.67,
            height: 11.67,
            left: 9.17,
            top: 4.17,
            position: 'absolute',
            background: '#0A0D14'
          }} />
        </div>
      </button>
      <div style={{
        flex: 1,
        height: 48,
        padding: '0 4px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 8,
        display: 'flex',
        flexWrap: 'wrap',
        alignContent: 'center',
        minWidth: 0
      }}>
        <input
          name={props.name}
          type="number"
          inputMode="decimal"
          value={state.qty}
          min={state.min}
          max={state.max}
          step={state.step}
          onChange={onChange}
          onKeyDown={onInputKeyDown}
          style={{
            color: '#0A0D14',
            fontSize: 16,
            fontFamily: 'Graphik Arabic',
            fontWeight: '400',
            lineHeight: 24,
            wordWrap: 'break-word',
            background: 'transparent',
            border: 'none',
            outline: 'none',
            width: '100%',
            minWidth: 20,
            textAlign: 'center',
            padding: 0
          }}
        />
      </div>
      <button
        type="button"
        onClick={onButtonClick('decrement')}
        onKeyDown={onButtonKeyDown}
        style={{
          minWidth: 36,
          width: 36,
          height: 48,
          justifyContent: 'center',
          alignItems: 'center',
          gap: 4,
          display: 'flex',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
          flexShrink: 0
        }}>
        <div style={{ width: 20, height: 20, position: 'relative' }}>
          <div style={{
            width: 11.67,
            height: 1.67,
            left: 4.17,
            top: 6.17,
            position: 'absolute',
            background: '#0A0D14'
          }} />
        </div>
      </button>
    </div>
  );
};
